import { api, ifMatch, unwrap } from "../../api/client";
import type { ClipOut, ClipPatch, PresetOut, RenderOut, TemplateOption, TryOut } from "../types";
import type { Params, ParamsByTemplate } from "./params";

/** POST /api/clips/{id}/renders with a caller-owned Idempotency-Key (reused when the user retries).
 * `params` = {template_id: params} of the tweaked templates ({} = every template as its default version). */
export function chooseTemplates(clipId: number, templateIds: string[], idempotencyKey: string,
  params: ParamsByTemplate = {}): Promise<RenderOut[]> {
  return unwrap(api.POST("/api/clips/{clip_id}/renders", {
    params: { path: { clip_id: clipId } }, body: { template_ids: templateIds, params },
    headers: { "Idempotency-Key": idempotencyKey },
  }));
}

/** Live tweak preview: one 9:16 still of this clip with one template and params at the first line-settle (up to 4
 * with `stills`), over the clip's own plate; never a video, cached server-side. Not a render: Choose / OK is. */
export function tryTemplate(clipId: number, templateId: string, params: Params, stills = 1): Promise<TryOut> {
  return unwrap(api.POST("/api/clips/{clip_id}/try", {
    params: { path: { clip_id: clipId } }, body: { template_id: templateId, params, stills },
  }));
}

export function listTries(clipId: number, templateId: string): Promise<TryOut[]> {
  return unwrap(api.GET("/api/clips/{clip_id}/tries", {
    params: { path: { clip_id: clipId }, query: { template_id: templateId, limit: 10 } },
  }));
}

export function savePreset(templateId: string, name: string, params: Params): Promise<PresetOut> {
  return unwrap(api.POST("/api/templates/{template_id}/presets", {
    params: { path: { template_id: templateId } }, body: { name, params },
  }));
}

export function skipClip(clip: Pick<ClipOut, "id" | "version">): Promise<ClipOut> {
  return unwrap(api.POST("/api/clips/{clip_id}/skip", { params: { path: { clip_id: clip.id }, header: ifMatch(clip.version) } }));
}

export function cancelRender(renderId: number): Promise<RenderOut> {
  return unwrap(api.POST("/api/renders/{render_id}/cancel", { params: { path: { render_id: renderId } } }));
}

/** Un-skip: puts the clip back into the template queue (`PATCH /api/clips/{id} {template_state: "needs_template"}`,
 * If-Match: the version the skip returned). */
export function unskipClip(clipId: number, version: number): Promise<ClipOut> {
  const body: ClipPatch = { template_state: "needs_template" };
  return unwrap(api.PATCH("/api/clips/{clip_id}", { params: { path: { clip_id: clipId }, header: ifMatch(version) }, body }));
}

export function disabledReason(o: TemplateOption): string | null {
  if (o.disabled_reason) return o.disabled_reason;
  if (o.template.disabled_reason) return o.template.disabled_reason;
  if (!o.template.available) return "template unavailable";
  if (!o.template.linux_certified) return "not certified on Linux";
  return null;
}
